<template>
  <van-dialog 
    v-model:show="visible" 
    title="问诊完成" 
    :show-confirm-button="false"
    :closeable="true"
    @close="handleClose"
    class-name="qr-dialog"
  >
    <div class="qr-content">
      <!-- 患者姓名 -->
      <p v-if="patientName" class="patient-name">患者：{{ patientName }}</p>

      <!-- 二维码Tab切换 -->
      <van-tabs 
        v-if="qrCodeList && qrCodeList.length > 0" 
        v-model:active="activeTab"
        class="qr-tabs"
        animated
        swipeable
      >
        <van-tab 
          v-for="(item, index) in qrCodeList" 
          :key="index"
          :title="item.title"
        >
          <div class="qr-container">
            <!-- 显示二维码 -->
            <div v-if="item.qrCode" class="qr-wrapper">
              <img 
                :src="item.qrCode" 
                :alt="item.title" 
                class="qr-image" 
                @load="handleImageLoad(item.type)" 
                @error="handleImageError(item.type)" 
              />
            </div>
            <!-- 失败状态：显示错误提示 -->
            <div v-else class="qr-error">
              <van-icon name="warning" size="48" color="#ee0a24" />
              <p>{{ item.title }}生成失败</p>
            </div>
          </div>
        </van-tab>
      </van-tabs>

      <!-- 无二维码时的提示 -->
      <div v-else class="qr-empty">
        <van-icon name="info" size="48" color="#969799" />
        <p>暂无二维码数据</p>
      </div>

      <!-- 截图保存说明 -->
      <div class="screenshot-guide" v-if="qrCodeList && qrCodeList.length > 0">
        <div class="guide-title">
          <van-icon name="photo" size="14" color="#1989fa" />
          <span>保存方式</span>
        </div>
        <div class="guide-item">
          <span>• 截图保存：同时按住电源键和音量减键</span>
        </div>
        <div class="guide-item">
          <span>• 长按二维码图片选择保存到相册</span>
        </div>
      </div>
    </div>
  </van-dialog>
</template>

<script setup>
import { ref, watch } from 'vue'
import { showToast } from 'vant'

const props = defineProps({
  show: {
    type: Boolean,
    default: false
  },
  qrCodeList: {
    type: Array,
    default: () => []
  },
  patientName: {
    type: String,
    default: ''
  }
})

const emit = defineEmits(['update:show', 'close'])

const visible = ref(false)
const activeTab = ref(0)

// 监听 show 属性变化
watch(() => props.show, (newVal) => {
  visible.value = newVal
  // 显示时重置到第一个tab
  if (newVal) {
    activeTab.value = 0
  }
})

// 监听 visible 变化，同步到父组件
watch(visible, (newVal) => {
  emit('update:show', newVal)
})

const handleClose = () => {
  visible.value = false
  emit('close')
}

const handleImageLoad = (type) => {
  console.log(`✅ ${type}二维码图片加载成功`)
}

const handleImageError = (type) => {
  console.error(`❌ ${type}二维码图片加载失败`)
  showToast({
    type: 'fail',
    message: `${type}二维码加载失败`
  })
}
</script>

<style lang="scss" scoped>
.qr-content {
  text-align: center;
  // 减少内边距，让二维码离边缘更近一点
  padding: 0 6px 12px 6px;
}

.patient-name {
  font-size: 16px;
  font-weight: 600;
  color: #323233;
  margin: 0 0 10px 0;
}

.qr-tabs {
  margin-bottom: 10px;
  
  :deep(.van-tabs__nav) {
    background-color: #fff;
  }
  
  :deep(.van-tab) {
    font-size: 15px;
    font-weight: 500;
  }
}

.qr-container {
  // 收紧上下留白，让二维码区域更集中
  padding: 6px 0;
  min-height: 180px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.qr-wrapper {
  width: 100%;
}

.qr-image {
  width: 100%;
  height: auto;
  display: block;
  margin: 0 auto;
  background-color: white;
  border-radius: 8px;
  // box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
}

.qr-error {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 12px;
  color: #646566;
  
  p {
    margin: 0;
    font-size: 14px;
  }
}

.qr-empty {
  padding: 40px 20px;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 12px;
  color: #969799;
  
  p {
    margin: 0;
    font-size: 14px;
  }
}

.screenshot-guide {
  background-color: #f7f8fa;
  border-radius: 6px;
  padding: 10px 12px;
  text-align: left;
  margin-top: 5px;

  .guide-title {
    display: flex;
    align-items: center;
    gap: 6px;
    margin-bottom: 6px;
    font-size: 13px;
    font-weight: 600;
    color: #323233;
  }

  .guide-item {
    margin-bottom: 4px;
    font-size: 12px;
    color: #646566;
    line-height: 1.4;

    &:last-child {
      margin-bottom: 0;
    }
  }
}

// 移动端优化
@media (max-width: 480px) {
  .qr-content {
    padding: 0 5px 10px 5px;
  }

}
</style>

<!-- 全局样式 -->
<style lang="scss">
.qr-dialog {
  .van-dialog__content {
    // 减少内容区左右留白
    padding: 10px 0 0 0 !important;
  }
  
  .van-dialog__header {
    padding: 15px 20px 0 20px !important;
    font-weight: 600;
  }
  
  // 调整关闭按钮位置
  .van-dialog__close {
    top: 12px;
    right: 12px;
    font-size: 22px;
    color: #c8c9cc;
  }
  
  // 确保对话框在移动端有合适的宽度
  @media (max-width: 480px) {
    // 减小与屏幕边缘的距离，使二维码更大
    width: calc(100vw - 32px) !important;
  }
}
</style>